import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Building2, ArrowLeft, ArrowRight, Check, Factory, Zap, Droplets,
  ChefHat, FlaskConical, Briefcase, Users, Shield, ChevronRight,
  Loader2, CheckCircle2, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/shared/Logo";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const INDUSTRIES = [
  { id: "manufacturing", label: "Manufacturing", icon: Factory, desc: "General manufacturing & assembly" },
  { id: "power_generation", label: "Power Generation", icon: Zap, desc: "Utilities & energy production" },
  { id: "water_treatment", label: "Water Treatment", icon: Droplets, desc: "Water & wastewater facilities" },
  { id: "food_processing", label: "Food Processing", icon: ChefHat, desc: "Food & beverage production" },
  { id: "chemicals", label: "Chemicals", icon: FlaskConical, desc: "Chemical processing & handling" },
  { id: "general", label: "Other Industry", icon: Briefcase, desc: "General industrial operations" },
];

const COMPANY_SIZES = [
  { id: "1-50", label: "1-50", desc: "Small team" },
  { id: "51-200", label: "51-200", desc: "Growing company" },
  { id: "201-500", label: "201-500", desc: "Mid-size enterprise" },
  { id: "501-1000", label: "501-1,000", desc: "Large enterprise" },
  { id: "1000+", label: "1,000+", desc: "Corporation" },
];

const STEPS = [
  { id: 1, label: "Business Info", icon: Building2 },
  { id: 2, label: "Compliance", icon: Shield },
  { id: 3, label: "Complete", icon: CheckCircle2 },
];

const OnboardingWizardPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Business Info
  const [companyName, setCompanyName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");

  // Step 2: Compliance
  const [compliances, setCompliances] = useState([]);
  const [selectedCompliances, setSelectedCompliances] = useState([]);
  const [loadingCompliances, setLoadingCompliances] = useState(false);

  // Step 3: Result
  const [onboardingResult, setOnboardingResult] = useState(null);

  // Auto-generate subdomain from company name
  useEffect(() => {
    if (companyName && !subdomain) {
      setSubdomain(companyName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }, [companyName]);

  // Fetch compliances when industry changes
  useEffect(() => {
    if (industry && step === 2) {
      fetchCompliances();
    }
  }, [industry, step]);

  // Fetch assessment data when entering step 3
  const fetchCompliances = async () => {
    setLoadingCompliances(true);
    try {
      const res = await api.get(`/onboarding/industry-compliances/${industry}`);
      setCompliances(res.data.compliances);
      setSelectedCompliances(res.data.compliances.map((c) => c.id));
    } catch (err) {
      toast.error("Failed to load compliance suggestions");
    } finally {
      setLoadingCompliances(false);
    }
  };

  const toggleCompliance = (id) => {
    setSelectedCompliances((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleNext = () => {
    if (step === 1) {
      if (!companyName.trim()) return toast.error("Company name is required");
      if (!subdomain.trim()) return toast.error("Subdomain is required");
      if (!/^[a-z0-9-]+$/.test(subdomain)) return toast.error("Subdomain can only contain lowercase letters, numbers, and hyphens");
      if (!industry) return toast.error("Please select your industry");
      if (!companySize) return toast.error("Please select your company size");
    }
    if (step === 2) {
      completeOnboarding();
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const completeOnboarding = async () => {
    const signupData = JSON.parse(sessionStorage.getItem("signup_data") || "{}");
    if (!signupData.email || !signupData.password || !signupData.fullName) {
      toast.error("Session expired. Please start over.");
      navigate("/auth/signup");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/onboarding/register", {
        email: signupData.email,
        password: signupData.password,
        full_name: signupData.fullName,
        company_name: companyName,
        company_subdomain: subdomain,
        industry,
        company_size: companySize,
        selected_compliance_ids: selectedCompliances,
        assessment_skipped: true,
        assessment_answers: {},
      });

      const { access_token, refresh_token, user, onboarding_summary } = res.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);

      setOnboardingResult({ user, summary: onboarding_summary });
      sessionStorage.removeItem("signup_data");
      setStep(3);

      toast.success("Welcome to Odesie!", {
        description: `${companyName} workspace is ready`,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed";
      toast.error("Onboarding failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const goToDashboard = () => {
    window.location.href = "/tenant/dashboard";
  };

  // ---- STEP RENDERERS ----

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: "'Sora', sans-serif" }}>
          Tell us about your business
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          We'll customize your training portal based on your industry
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="company-name">Company Name</Label>
          <Input
            id="company-name"
            placeholder="e.g. Acme Manufacturing"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="h-11 bg-background/50 border-border/50"
            data-testid="onboarding-company-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="subdomain">Workspace URL</Label>
          <div className="flex items-center gap-0">
            <Input
              id="subdomain"
              placeholder="acme"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              className="h-11 bg-background/50 border-border/50 rounded-r-none"
              data-testid="onboarding-subdomain"
            />
            <span className="h-11 px-3 flex items-center bg-muted border border-l-0 border-border/50 rounded-r-md text-xs text-muted-foreground whitespace-nowrap">
              .odesie.com
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Industry</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {INDUSTRIES.map((ind) => (
            <button
              key={ind.id}
              onClick={() => setIndustry(ind.id)}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                industry === ind.id
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border/50 hover:border-muted-foreground/30 bg-background/30"
              }`}
              data-testid={`onboarding-industry-${ind.id}`}
            >
              <ind.icon className={`w-5 h-5 mb-1.5 ${industry === ind.id ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-medium text-sm text-foreground">{ind.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{ind.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Company Size</Label>
        <div className="flex flex-wrap gap-2">
          {COMPANY_SIZES.map((size) => (
            <button
              key={size.id}
              onClick={() => setCompanySize(size.id)}
              className={`px-4 py-2.5 rounded-lg border-2 transition-all duration-200 ${
                companySize === size.id
                  ? "border-primary bg-primary/5 text-primary font-semibold"
                  : "border-border/50 hover:border-muted-foreground/30 text-foreground"
              }`}
              data-testid={`onboarding-size-${size.id}`}
            >
              <div className="text-sm font-medium">{size.label}</div>
              <div className="text-xs text-muted-foreground">{size.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5 animate-fade-in">
      <div className="text-center mb-2">
        <h2 className="text-xl font-extrabold text-foreground tracking-tight">
          Recommended Compliance Training
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Based on <span className="font-semibold text-blue-500">{INDUSTRIES.find((i) => i.id === industry)?.label}</span>, we recommend these compliance areas
        </p>
      </div>

      {/* Select All / Count */}
      <div className="flex items-center justify-between px-1">
        <button
          onClick={() => {
            if (selectedCompliances.length === compliances.length) setSelectedCompliances([]);
            else setSelectedCompliances(compliances.map(c => c.id));
          }}
          className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
        >
          {selectedCompliances.length === compliances.length ? "Deselect All" : "Select All"}
        </button>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600">
          {selectedCompliances.length} of {compliances.length} selected
        </span>
      </div>

      {loadingCompliances ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-muted-foreground">Loading recommendations...</span>
        </div>
      ) : (
        <div className="space-y-2.5">
          {compliances.map((comp, idx) => {
            const isSelected = selectedCompliances.includes(comp.id);
            const catColor = comp.category === "Safety" ? { bg: "bg-red-50", text: "text-red-600", border: "border-red-100" }
              : comp.category === "Compliance" ? { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100" }
              : { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100" };
            return (
              <button
                key={comp.id}
                onClick={() => toggleCompliance(comp.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left group ${
                  isSelected
                    ? "border-blue-300 bg-blue-50/40 shadow-sm"
                    : "border-slate-200 hover:border-slate-300 bg-white hover:shadow-sm"
                }`}
                style={{ animationDelay: `${idx * 50}ms` }}
                data-testid={`compliance-${comp.id}`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                    isSelected ? "bg-blue-500 border-blue-500 scale-110" : "border-slate-300 group-hover:border-slate-400"
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm text-slate-900">{comp.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${catColor.bg} ${catColor.text} border ${catColor.border}`}>
                        {comp.category}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{comp.description}</p>
                  </div>
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2 animate-pulse" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-blue-50/60 border border-blue-100">
        <Shield className="w-4 h-4 text-blue-500 shrink-0" />
        <p className="text-xs text-slate-600">
          Selected compliances will be set up as <span className="font-semibold">90-day mandatory</span> training templates for your organization
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (!onboardingResult) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }
    const { summary } = onboardingResult;

    return (
      <div className="space-y-6 animate-fade-in text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-green-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: "'Sora', sans-serif" }}>
            Welcome to Odesie!
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your workspace <span className="text-primary font-medium">{summary.company_name}</span> is ready
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
            <Shield className="w-5 h-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">{summary.compliance_templates_created}</div>
            <div className="text-xs text-muted-foreground">Compliance Templates</div>
          </div>
          <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
            <Users className="w-5 h-5 text-accent mb-2" />
            <div className="text-2xl font-bold text-foreground">{summary.departments_created?.length || 0}</div>
            <div className="text-xs text-muted-foreground">Departments Created</div>
          </div>
        </div>

        <Button
          onClick={goToDashboard}
          className="w-full h-12 text-base font-semibold btn-primary"
          data-testid="go-to-dashboard-btn"
        >
          Go to Dashboard
          <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl floating" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl floating-delayed" />
      </div>

      <div className="w-full max-w-2xl relative z-10 animate-fade-in">
        {/* Back Button */}
        {step < 3 && (
          <button
            onClick={step === 1 ? () => navigate("/auth/role") : handleBack}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? "Back to role selection" : "Previous step"}
          </button>
        )}

        <div className="glass-card p-6 md:p-8">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <Logo size="lg" />
          </div>

          {/* Step indicator */}
          {step < 3 && (
            <div className="flex items-center justify-center gap-1 mb-6">
              {STEPS.slice(0, 2).map((s, idx) => (
                <div key={s.id} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    step === s.id
                      ? "bg-primary text-primary-foreground"
                      : step > s.id
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {step > s.id ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {idx < 1 && <ChevronRight className="w-4 h-4 text-muted-foreground mx-1" />}
                </div>
              ))}
            </div>
          )}

          {/* Step Content */}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation */}
          {step < 3 && (
            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleNext}
                disabled={submitting}
                className="h-11 px-8 font-semibold btn-primary"
                data-testid="onboarding-next-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {step === 2 ? "Complete Setup" : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizardPage;
